/**
 * ProtectedRoutesV2.jsx
 *
 * V2 route tree using React Router v6 layout-route pattern.
 *
 * ┌─ Routes ──────────────────────────────────────────────────────────────┐
 * │  /sso/callback     → SSO           (public — no auth needed)          │
 * │  /marketcast       → SSOLogin      (public — no auth needed)          │
 * │                                                                        │
 * │  [AuthGuard]       ← handles SSO redirect / spinner / login page      │
 * │    [LayoutV2]      ← fixed header + sidebar + <Outlet /> for content  │
 * │      /             → <Navigate to="/jobs-library" />                   │
 * │      /jobs-library → JobsLibraryV2  (via EntitlementGuard)            │
 * │      /dashboard    → Dashboard      (via EntitlementGuard)            │
 * │      ...all other routes from routeConfigV2                           │
 * │      /not-authorized → NotAuthorized                                   │
 * │      *             → <Navigate to="/" />                               │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * Adding a new page:
 *   1. Import the component in RouteConfigV2.js
 *   2. Add an entry to `routeConfigV2`
 *   Done — no changes needed in this file.
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { showRemainingNotification } from 'Store/actions/notificationActions';
import { getAllPendingNotifications } from 'Api/apiService';

import SSO from 'Pages/SSO/SSO';
import MarketCastHome from 'Pages/SSO/SSOLogin';
import NotAuthorized from 'Pages/NotAuthorized';
import LayoutV2 from 'Components/LayoutV2/LayoutV2';
import AuthGuard from 'Routes/guards/AuthGuard';
import EntitlementGuard from 'Routes/guards/EntitlementGuard';
import { routeConfigV2, isWipOnly } from 'Routes/RouteConfigV2';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import { useHasAccess } from 'Routes/hooks/useHasAccess';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * First routeConfigV2 entry the current role can access, preferring a real
 * (non-WIP) module over a WIP one (isWipOnly — see RouteConfigV2.js) so a
 * role holding e.g. "dashboard:inprogress" doesn't get landed on a WIP page
 * ahead of a real module it also has access to. Falls back to the WIP entry
 * only if that's the sole thing the role can access. Shared by SmartRedirect
 * and the role-switch effect below — both redirect a role to "the first page
 * it owns" and must agree on what that page is.
 *
 * Only considers routes with a `resource` — resource-less utility routes
 * (e.g. /legal) return true from hasAccess unconditionally, so without this
 * guard they'd be picked as the "first accessible" route and hijack the
 * landing.
 */
const firstAccessibleRoute = (hasAccess) => {
  const accessibleRoutes = routeConfigV2.filter(
    ({ resource, requiredEntitlements }) => resource && hasAccess(resource, requiredEntitlements)
  );
  return (
    accessibleRoutes.find(({ requiredEntitlements }) => !isWipOnly(requiredEntitlements)) ??
    accessibleRoutes[0]
  );
};

/**
 * Redirects to the first route the current role can access.
 * Used as the root index route so every role lands on a page they own
 * instead of always hitting /jobs-library.
 */
const SmartRedirect = () => {
  const { hasAccess } = useHasAccess();
  const firstAccessible = firstAccessibleRoute(hasAccess);
  return <Navigate to={firstAccessible?.path ?? '/not-authorized'} replace />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ProtectedRoutesV2 = () => {
  const { entitlements, } = useEntitlementsStore();
  const { hasAccess } = useHasAccess();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Role-switch redirect.
   *
   * Fires whenever entitlements change (i.e. the user switched roles).
   * Two cases handled:
   *   1. User is on /not-authorized — if the new role has accessible routes,
   *      send them there instead of leaving them stuck on the error page.
   *   2. User is on a route that the new role cannot access — redirect to the
   *      first route they can access (EntitlementGuard also handles this
   *      synchronously, but this acts as a safety net for non-routeConfigV2 paths).
   */
  useEffect(() => {
    if (!entitlements?.length) return;

    const isOnNotAuthorized = location.pathname === '/not-authorized';

    const currentRouteConfig = routeConfigV2.find(({ path }) =>
      matchPath(path, location.pathname) !== null
    );

    const currentRouteBlocked =
      currentRouteConfig?.resource &&
      !hasAccess(currentRouteConfig.resource, currentRouteConfig.requiredEntitlements);

    if (isOnNotAuthorized || currentRouteBlocked) {
      const firstAccessible = firstAccessibleRoute(hasAccess);
      if (firstAccessible) {
        navigate(firstAccessible.path, { replace: true });
      }
      // No firstAccessible → role genuinely has no modules, stay on /not-authorized
    }
  }, [entitlements]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Fetch pending notifications once entitlements are available (user is
   * authenticated). Defined inside the effect to avoid stale closure issues
   * and to keep `dispatch` as the only stable external dependency.
   */
  useEffect(() => {
    if (!entitlements?.length) return;

    const fetchPendingNotifications = async () => {
      try {
        const notifications = await getAllPendingNotifications();
        if (notifications?.length) {
          // Reverse so the oldest notification appears first (FIFO display order)
          [...notifications]
            .reverse()
            .forEach(notify => dispatch(showRemainingNotification(notify)));
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };

    fetchPendingNotifications();
  }, [entitlements, dispatch]);

  return (
    <Routes>
      {/* ── Public routes (no authentication required) ─────────────────────── */}
      <Route path="/sso/callback" element={<SSO />} />
      <Route path="/marketcast" element={<MarketCastHome />} />

      {/* ── Auth-gated routes ──────────────────────────────────────────────── */}
      {/*
       * AuthGuard  — pathless layout route that checks the token.
       *              Shows loading spinner, login page, or renders <Outlet />.
       *
       * LayoutV2   — pathless layout route that renders the shell UI:
       *              fixed header + sidebar + <Outlet /> (page content area).
       *
       * Both are pathless so they match every URL below them, which is the
       * standard React Router v6 "layout route" pattern. Adding a new page
       * only requires a new entry in routeConfigV2 — nothing changes here.
       */}
      <Route element={<AuthGuard />}>
        <Route element={<LayoutV2 />}>

          {/* Root: redirect to first route accessible by the current role */}
          <Route index element={<SmartRedirect />} />

          {/* App routes — sourced from RouteConfigV2 */}
          {routeConfigV2.map(({ path, element, resource, requiredEntitlements, resolveElement }) => (
            <Route
              key={path}
              path={path}
              element={
                <EntitlementGuard
                  resource={resource}
                  requiredEntitlements={requiredEntitlements}
                  resolveElement={resolveElement}
                >
                  {element}
                </EntitlementGuard>
              }
            />
          ))}
          {/* Utility routes */}
          <Route path="/not-authorized" element={<NotAuthorized />} />
          <Route path="*" element={<Navigate to="/" replace />} />

        </Route>
      </Route>
    </Routes>
  );
};

export default ProtectedRoutesV2;